import { inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';

import { CalendarView, UserSettings } from '@models';
import {
  AppState,
  resetCommunity,
  resetRoot,
  resetViews,
  setCalendarView,
  setCommunityData,
  setDefaultView,
  setGroupsView,
  setRootData,
  setUsersView,
} from '@store';

@Service()
export class SettingsService {
  store = inject<Store<AppState>>(Store);

  views!: {
    defaultView?: number;
    usersView?: number;
    groupsView?: number;
    roomTreeView?: number;
    calendarView?: CalendarView;
  };

  constructor() {
    this.store
      .select((state) => state)
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        this.views = state.views;
      });
  }

  setRootData = (settingDataObj: UserSettings) => {
    this.setCommunity(settingDataObj);
    this.setRoot(settingDataObj);

    if (settingDataObj.preferences) {
      if (this.views.defaultView === undefined) {
        this.setRoomDefaultView(settingDataObj.preferences.defaultView ?? 0);
      }

      if (this.views.usersView === undefined) {
        if (settingDataObj.preferences.usersView !== undefined) {
          this.setUsersView(settingDataObj.preferences.usersView);
        } else {
          this.setUsersView(settingDataObj.preferences.defaultView ?? 0);
        }
      }

      if (this.views.groupsView === undefined) {
        if (settingDataObj.preferences.groupsView !== undefined) {
          this.setGroupsView(settingDataObj.preferences.groupsView);
        } else {
          this.setGroupsView(settingDataObj.preferences.defaultView ?? 0);
        }
      }

      if (this.views.calendarView === undefined) {
        if (settingDataObj.preferences.calendarDefaultView) {
          this.setCalendarDefaultView(
            settingDataObj.preferences.calendarDefaultView
          );
        }
      }
    }
  };

  resetRootData = () => {
    this.store.dispatch(resetViews());
    this.store.dispatch(resetRoot());
    this.store.dispatch(resetCommunity());
  };

  setCommunity = (data: UserSettings) => {
    const community = {
      communityId: data.communityId,
      communityPermission: data.communityPermission,
      communityExpireDate: data.communityExpireDate,
      communityName: data.communityName,
      communityProfileId: data.communityProfileId,
      communityReporting: data.communityReporting,
      communitySignature: data.communitySignature,
      communityStatus: data.communityStatus,
      communitySubtype: data.communitySubtype,
      communityTimeout: data.communityTimeout,
    };

    this.store.dispatch(setCommunityData({ payload: community }));
  };

  setRoot = (data: UserSettings) => {
    const root = {
      rootGroupsId: data.rootGroupsId,
      rootGroupsPermission: data.rootGroupsPermission,
      rootRoomsId: data.rootRoomsId,
      rootRoomsPermission: data.rootRoomsPermission,
      rootUsersId: data.rootUsersId,
      rootUsersPermission: data.rootUsersPermission,
      signingRoomId: data.signingRoomId,
      signingRoomPermission: data.signingRoomPermission,
    };

    this.store.dispatch(setRootData({ payload: root }));
  };

  setRoomDefaultView = (defaultView: number) => {
    this.store.dispatch(setDefaultView({ payload: defaultView }));
  };

  getRoomDefaultView = () => this.views.defaultView;

  setUsersView = (usersView: number) => {
    this.store.dispatch(setUsersView({ payload: usersView }));
  };

  setGroupsView = (groupsView: number) => {
    this.store.dispatch(setGroupsView({ payload: groupsView }));
  };

  setCalendarDefaultView = (calendarView: CalendarView) => {
    this.store.dispatch(setCalendarView({ payload: calendarView }));
  };
}
